import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import { CONTENT_SHAPE } from '../constants/contentColors';
import { ZERO_ICONS } from '../constants/zeroIcons';
import { contentBorders } from '../theme/createContentTheme';

export interface EmptyStateProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: SvgIconComponent;
  action?: React.ReactNode;
  /** Keep the footprint of the content it replaces so the layout doesn't jump. */
  minHeight?: number | string;
  /** `panel` draws a dashed frame; `inline` sits inside an existing panel. */
  variant?: 'panel' | 'inline';
  sx?: SxProps<Theme>;
}

/** Quiet, typographic empty state for sections, charts and tables. */
const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon: Icon = ZERO_ICONS.insights,
  action,
  minHeight = 160,
  variant = 'panel',
  sx,
}) => (
  <Box
    role="status"
    sx={[
      (theme) => ({
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1,
        px: 3,
        py: 4,
        ...(variant === 'panel'
          ? {
              border: `1px dashed ${contentBorders(theme).strong}`,
              borderRadius: `${CONTENT_SHAPE.md}px`,
              bgcolor: 'background.paper',
            }
          : {}),
      }),
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    <Icon aria-hidden sx={{ fontSize: 24, color: 'text.disabled' }} />
    <Typography variant="subtitle2" component="p" sx={{ color: 'text.primary' }}>
      {title}
    </Typography>
    {description ? (
      <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '56ch' }}>
        {description}
      </Typography>
    ) : null}
    {action ? <Box sx={{ mt: 1 }}>{action}</Box> : null}
  </Box>
);

export default EmptyState;
